import { z } from "zod";

/** `path` — JSON pointer к полю документа/параметров. */
export const issueSchema = z.object({
  path: z.string(),
  code: z.string(),
  message: z.string(),
});
export type Issue = z.infer<typeof issueSchema>;

/**
 * Ошибка валидатора запроса FastAPI: `{loc, type}` без значений поля (их не эхоят — 09-security.md).
 * Прилетает на том же коде `validation_error`, что и issue конфигурации, но формат другой.
 */
export const requestFieldErrorSchema = z.object({
  loc: z.array(z.string()),
  type: z.string(),
});
export type RequestFieldError = z.infer<typeof requestFieldErrorSchema>;

/**
 * `error.details` у 422 бывает двух видов, и схема обязана принимать оба, иначе разбор конверта
 * падает целиком и клиент теряет и сообщение сервера, и `request_id`, и код ошибки:
 * issue документа (`{path, code, message}`) и ошибка полей запроса (`{loc, type}`).
 */
export const errorDetailSchema = z.union([issueSchema, requestFieldErrorSchema]);
export type ErrorDetail = z.infer<typeof errorDetailSchema>;

export function isIssue(detail: ErrorDetail): detail is Issue {
  return "path" in detail;
}

/** Единый конверт ошибки admin API — docs/api/admin-api-v1.md, docs/tz/03-backend.md §6. */
export const apiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    request_id: z.string(),
    // Неизвестная форма элемента не должна ронять разбор конверта: лишние элементы отбрасываем,
    // сообщение и request_id важнее, чем детали.
    details: z.array(z.unknown()).optional(),
  }),
});
export type ApiErrorBody = z.infer<typeof apiErrorSchema>;

/** Разбирает `error.details` на issue документа и ошибки полей запроса, молча пропуская чужое. */
export function splitErrorDetails(details: unknown[] | undefined): {
  issues: Issue[];
  fieldErrors: RequestFieldError[];
} {
  const issues: Issue[] = [];
  const fieldErrors: RequestFieldError[] = [];
  for (const item of details ?? []) {
    const parsed = errorDetailSchema.safeParse(item);
    if (!parsed.success) continue;
    if (isIssue(parsed.data)) issues.push(parsed.data);
    else fieldErrors.push(parsed.data);
  }
  return { issues, fieldErrors };
}

/** Доля с 95% интервалом Уилсона. */
export const rateSchema = z.object({
  value: z.number(),
  ci_low: z.number(),
  ci_high: z.number(),
  n: z.number().int().nonnegative(),
});
export type Rate = z.infer<typeof rateSchema>;

export function paginated<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    next_cursor: z.string().nullable().optional(),
  });
}

export const roleSchema = z.enum(["owner", "admin", "editor", "analyst", "viewer"]);
export type Role = z.infer<typeof roleSchema>;

/** Порядок ролей по убыванию привилегий — owner > admin > editor > analyst > viewer. */
export const ROLE_RANK: Record<Role, number> = {
  owner: 4,
  admin: 3,
  editor: 2,
  analyst: 1,
  viewer: 0,
};

export function roleAtLeast(role: Role, minimum: Role): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[minimum];
}
